var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useCallback, useMemo } from 'react';
import isEqual from 'lodash/isEqual';
import { useLocation } from 'react-router';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { removeNullishValues } from '@business/utils/object';
import TimeUtils from '@business/utils/time';
import useAsyncAction from '@data/hooks/useAsyncAction';
import { useStore } from '@data/other/withStore';
import { Routes as AppRoutes, getLanguageSegment, getPropertyRoot, withLanguageSegment, } from '@view/routes';
import { getAllSearchParams } from '@view/utils/search-params';
import store from '@data/stores/Store';
import { scrollToTop } from '@view/utils/scroll-to-top';
import { useApplicationFlow } from './useApplicationFlow';
import { useAppParams } from './useAppParams';
export const usePageNavigation = () => {
    const navigate = useNavigate();
    const { currentRoute, nextRoute } = useApplicationFlow();
    const { pathname } = useLocation();
    const { environment: { language }, components, components: { searchPanel: { value: { checkin, checkout }, }, }, property, property: { addons, cart }, } = useStore();
    const { propertyCode } = useAppParams();
    const [searchParams, setSearchParams] = useSearchParams({});
    const currentParams = getAllSearchParams(searchParams);
    const [searchAddons] = useAsyncAction(useCallback(addons.search, []));
    const isHomePage = useMemo(() => {
        const root = getPropertyRoot(pathname);
        const path = pathname.endsWith('/')
            ? pathname.substring(0, pathname.length - 1)
            : pathname;
        return path === root && (!checkin || !checkout);
    }, [checkin, checkout, pathname]);
    const isAccommodationsPage = useMemo(() => Boolean(pathname.includes(AppRoutes.Accommodations) && checkin && checkout), [checkin, checkout, pathname]);
    const isAddonsPage = useMemo(() => Boolean(pathname.includes(AppRoutes.Addons) && checkin && checkout), [checkin, checkout, pathname]);
    const isGuestPage = useMemo(() => pathname.includes(AppRoutes.Guests), [pathname]);
    const isPaymentPage = useMemo(() => pathname.includes(AppRoutes.Payment), [pathname]);
    const isConfirmationPage = useMemo(() => pathname.includes(AppRoutes.Confirmation), [pathname]);
    const setCurrency = useCallback((currency) => {
        setSearchParams(Object.assign(Object.assign({}, currentParams), { currency }), { replace: true });
    }, [currentParams, setSearchParams]);
    const removeSearchParams = useCallback((params) => {
        const arr = Array.isArray(params) ? params : [params];
        const newParams = Object.assign({}, currentParams);
        arr.forEach((param) => {
            delete newParams[param];
        });
        setSearchParams(newParams, { replace: true });
    }, [currentParams, setSearchParams]);
    const setLanguage = useCallback((language) => {
        if (getLanguageSegment(pathname) === language) {
            return;
        }
        const query = Object.keys(currentParams)
            .map((key) => `${key}=${currentParams[key]}`)
            .join('&');
        const url = `${withLanguageSegment(pathname, language)}${query.length > 0 ? `?${query}` : ''}`;
        navigate(url, { replace: true });
    }, [navigate, pathname, currentParams]);
    const goTo = (path, from, query, options) => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b;
        const next = (url) => {
            const root = getPropertyRoot(pathname);
            if (url !== root) {
                url = root + '/' + url;
            }
            url = url.replace(':propertyCode', propertyCode || '');
            if (query !== undefined) {
                const queryItems = [];
                for (const item in query) {
                    if (query[item])
                        queryItems.push(item + '=' + query[item]);
                }
                if (queryItems.length) {
                    url += '?' + queryItems.join('&');
                }
            }
            navigate(url, options);
            if (!store.isImmersivePersistenceEnabled()) {
                store.savePersistentData();
            }
        };
        if (path === AppRoutes.Addons) {
            if (from !== AppRoutes.Accommodations) {
                const totalAddons = ((_a = addons.model) === null || _a === void 0 ? void 0 : _a.items.length) || 0;
                if (totalAddons === 0) {
                    return next(AppRoutes.Accommodations);
                }
            }
            else {
                const addonsSearchPayload = {
                    accommodations: cart.accommodations.map((accommodation) => ({
                        adults: accommodation.adults,
                        kids: accommodation.kids,
                        packageId: accommodation.item.packageId,
                        roomTypeId: accommodation.item.id,
                    })),
                    checkin: TimeUtils.format(components.searchPanel.value.checkin),
                    checkout: TimeUtils.format(components.searchPanel.value.checkout),
                    language,
                    propertyId: ((_b = property.model) === null || _b === void 0 ? void 0 : _b.id) || 0,
                };
                // @TECHDEBT the usePageNavigation hook shouldn't be in charge of fetching the addons since it's out of its scope
                const addonList = yield searchAddons(addonsSearchPayload);
                if (addonList && addonList.items.length === 0) {
                    return next(AppRoutes.Guests);
                }
            }
        }
        next(path);
    });
    const goBack = () => {
        navigate(-1);
        scrollToTop();
    };
    /**
     * Redirects to AppRoutes.Accommodations keeping the current parameters, this will render Accommodations list if search params are set.
     * Used by page components that require having items in the cart (Addons, Guest, Payment) to redirect if the cart is empty.
     */
    const goToHomePage = (query) => {
        const newParams = removeNullishValues(Object.assign(Object.assign({}, currentParams), query));
        goTo(AppRoutes.Accommodations, currentRoute, newParams, {
            replace: true,
        });
    };
    const goToPaymentPage = (query, { replace = true } = {}) => {
        const newParams = removeNullishValues(Object.assign(Object.assign({}, currentParams), query));
        goTo(AppRoutes.Payment, currentRoute, newParams, { replace });
    };
    /**
     * Resets query parameters to redirect to AppRoutes.Accommodations and show the landing page
     */
    const goToLandingPage = () => {
        goTo(AppRoutes.Accommodations, currentRoute, {
            checkin: '',
            checkout: '',
            promo: '',
        }, { replace: true });
        scrollToTop();
    };
    /**
     * Redirects to the next route depending on the application flow based on the property setting to display addons
     */
    const goNext = (query) => {
        const newParams = removeNullishValues(Object.assign(Object.assign({}, currentParams), query));
        if (nextRoute === AppRoutes.Confirmation) {
            return goTo(nextRoute, currentRoute, newParams);
        }
        // If the query parameters changed, redirect to Accommodations route without generating a new history entry
        if (!isEqual(newParams, currentParams)) {
            return goTo(AppRoutes.Accommodations, currentRoute, newParams, {
                replace: !!currentParams.checkin && !!currentParams.checkout,
            });
        }
        // Go to next route
        return goTo(nextRoute, currentRoute, newParams);
    };
    const redirectNotFound = (errorCode) => {
        const url = `/404?redirect_url=${encodeURIComponent(window.location.href)}${errorCode ? `&error_code=${errorCode}` : ''}`;
        return navigate(url, { replace: true });
    };
    return {
        goBack,
        goNext,
        goToHomePage,
        goToLandingPage,
        goToPaymentPage,
        isAccommodationsPage,
        isAddonsPage,
        isConfirmationPage,
        isGuestPage,
        isHomePage,
        isPaymentPage,
        redirectNotFound,
        removeSearchParams,
        setCurrency,
        setLanguage,
    };
};
